import React from 'react';
import { LocalServicePage } from '../../components/LocalServicePage';

export const LocksmithIlford = () => {
  return (
    <LocalServicePage
      areaName="Ilford"
      areaType="City"
      slug="locksmith-ilford"
      heroTitle="Door, Window & Lock Specialists in Ilford"
      heroSubtitle="Royal Security provides expert door, window, and lock repair services throughout Ilford and the wider Redbridge area. We combine specialist knowledge with honest, straightforward service."
      introContent={
        <>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Ilford's position as a major residential centre means a wide variety of properties—Edwardian family homes, inter-war semis, post-war estates, and newer developments. Each brings different door and window systems, and we've developed expertise across all of them through years of working in the area.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our approach is simple: diagnose properly, explain clearly, and fix thoroughly. We find the root cause of problems rather than applying temporary solutions. Whether it's a Victorian bay window mechanism, a composite front door lock, or a modern bi-fold system, we take the same careful approach.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We serve homeowners, landlords, and property managers across Ilford. Many of our customers have used us for years and recommended us to others—that reputation for honest, reliable service is what drives our business.
          </p>
        </>
      }
      servicesEmphasis={[
        {
          title: "Multipoint Lock Repairs & Replacement",
          description: "The multipoint locks on modern doors are essential for security but can develop faults. We repair gearboxes, replace hooks and rollers, and fit complete new mechanisms when necessary—always diagnosing the actual problem first."
        },
        {
          title: "Composite & uPVC Door Services",
          description: "Whether you have a composite front door or uPVC throughout, we handle all repairs—locks, handles, hinges, weather seals, panels, and hardware. We restore doors to reliable, secure operation."
        },
        {
          title: "Window Repairs All Types",
          description: "Casement windows, tilt and turn systems, sliding sashes—we repair mechanisms and hardware for all window types. Friction stays, espagnolettes, tilt mechanisms, handles, and locks."
        },
        {
          title: "Aluminium Systems",
          description: "Ilford has many properties with aluminium windows, bi-folds, and sliding doors. These require specific expertise and parts. We service, repair, and maintain aluminium systems to ensure long-term performance."
        },
        {
          title: "Glazing Repairs",
          description: "Failed sealed units, misted double glazing, and damaged glass are common issues. We replace glass within existing frames, with options for upgraded thermal or acoustic performance."
        },
        {
          title: "Security Assessment",
          description: "Concerned about your property's security? We assess doors and windows and recommend practical upgrades—better locks, additional security features, and improved hardware where beneficial."
        }
      ]}
      localContextTitle="Simple Checks Before You Book a Repair in Ilford"
      localContext={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Sticking doors and windows:</strong> Note whether the problem is worse in hot or cold weather. uPVC and composite doors expand and contract, and a door that only sticks in warmer weather may need hinge and keep adjustment rather than new parts—we'll confirm on inspection.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Stiff locks:</strong> Don't force a handle or key that resists—forcing it can break a worn gearbox or cylinder completely. Try pulling or pushing the door against its seals while locking. If that helps, alignment may be involved—mention it when you call.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            <strong>Misted glazing:</strong> Condensation <em>between</em> the panes indicates a failed sealed unit, which can normally be replaced without changing a frame that's in good condition. Condensation on the room-side surface can involve humidity, ventilation, heating or the glass's insulating performance, so it isn't a diagnosis on its own. We measure on the first visit and fit any replacement unit once it's made.
          </p>
        </>
      }
      commonProblems={[
        {
          title: "Door handle operating stiffly",
          description: "Usually indicates gearbox wear or spring failure. Often repairable without full mechanism replacement."
        },
        {
          title: "Windows not closing securely",
          description: "Lock mechanism or hinge issues. We diagnose and repair to restore proper security."
        },
        {
          title: "Composite door swelling in heat",
          description: "A known characteristic of composite doors. Adjustment and sometimes component changes resolve this."
        },
        {
          title: "Draught from older windows",
          description: "Seal degradation and compression loss. New seals and adjustment often restore performance."
        },
        {
          title: "Patio door jumping off track",
          description: "Roller or track wear. We service and repair to restore smooth, secure operation."
        },
        {
          title: "Double glazing cloudy between panes",
          description: "Seal failure in the unit. We replace the glass while keeping your frames."
        }
      ]}
      whyChooseUs={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Ilford customers expect value—quality work at fair prices. That's exactly what we provide. We don't inflate quotes or recommend unnecessary replacements. Our business model depends on reputation and repeat customers, so honest dealing is fundamental to how we operate.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our diagnostic approach saves customers money. By identifying the actual cause of problems, we repair efficiently rather than replacing components that don't need it. A simple adjustment is always better than a full mechanism replacement if it solves the problem.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We're accountable for our work. If a repair doesn't solve the problem, we come back and address it. Using quality parts and working carefully means this rarely happens—but knowing we stand behind our work gives customers confidence.
          </p>
        </>
      }
      ctaText="Looking for Door & Window Specialists in Ilford?"
    />
  );
};
